import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { adminAPI } from '../../services/api';
import './AdminPages.css';
import './Analytics.css';

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function truncateLabel(p, max = 40) {
  if (!p || p.length <= max) return p || '—';
  return `${p.slice(0, max)}…`;
}

/** نصوص المحاور والوسوم: رمادي فاتح بدل الأسود الافتراضي لـ Recharts */
const axisTick = { fill: '#c5cdd9', fontSize: 11 };
const axisTickSm = { fill: '#c5cdd9', fontSize: 10 };
const axisLine = { stroke: '#e8ecf1' };
const legendStyle = { color: '#c5cdd9', fontSize: 12 };
const tooltipContentStyle = {
  borderRadius: 8,
  border: '1px solid #e8ecf1',
  color: '#6b7280',
};

const Analytics = () => {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topCars, setTopCars] = useState([]);
  /** true إذا رجع الخادم 404 لـ top-variants (نسخة قديمة من الـ API) — نعرض المسارات كاحتياطي */
  const [carsChartIsPathFallback, setCarsChartIsPathFallback] = useState(false);
  const [devices, setDevices] = useState([]);
  const [series, setSeries] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { from, to };
    try {
      const [ov, dev, ts, topCarsWrap] = await Promise.all([
        adminAPI.getAnalyticsOverview(params),
        adminAPI.getAnalyticsDevices(params),
        adminAPI.getAnalyticsTimeseries(params),
        adminAPI
          .getAnalyticsTopVariants({ ...params, limit: 8 })
          .then((r) => ({ ok: true, data: r.data }))
          .catch((e) => {
            if (e.response?.status === 404) return { ok: false, notFound: true };
            throw e;
          }),
      ]);
      setOverview(ov.data);
      setDevices(dev.data?.results || []);
      setSeries(ts.data?.series || []);

      if (topCarsWrap.ok) {
        setCarsChartIsPathFallback(false);
        setTopCars(topCarsWrap.data?.results || []);
      } else {
        const tp = await adminAPI.getAnalyticsTopPages({ ...params, limit: 8 });
        setCarsChartIsPathFallback(true);
        setTopCars(
          (tp.data?.results || []).map((row) => ({
            label: row.path || '—',
            pageviews: row.pageviews,
          }))
        );
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || e.message || 'فشل التحميل');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  const chartData = useMemo(
    () =>
      (series || []).map((row) => ({
        day: row.date?.slice(5) || row.date,
        sessions: row.sessions,
        pageviews: row.pageviews,
      })),
    [series]
  );

  const carsBarData = useMemo(
    () =>
      (topCars || []).map((row) => ({
        label: truncateLabel(row.label),
        views: row.pageviews,
      })),
    [topCars]
  );

  const devicesBarData = useMemo(
    () =>
      (devices || []).map((row) => ({
        label: row.device_category || '—',
        sessions: row.sessions,
      })),
    [devices]
  );

  const avgPagesPerSession =
    overview && overview.sessions > 0 ? (overview.pageviews / overview.sessions).toFixed(1) : null;

  const lineEmpty =
    chartData.length === 0 || chartData.every((d) => d.pageviews === 0 && d.sessions === 0);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>إحصائيات الزيارات</h1>
      </div>

      <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <label>
          من{' '}
          <input
            type="date"
            value={from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          />
        </label>
        <label>
          إلى{' '}
          <input
            type="date"
            value={to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          />
        </label>
        <button type="button" className="btn btn-primary" onClick={load} disabled={loading}>
          تحديث
        </button>
      </div>

      {error && (
        <div
          className="admin-error-banner"
          style={{ marginTop: '12px', padding: '12px', background: '#fee', borderRadius: '8px' }}
        >
          {error}
        </div>
      )}

      {loading && !overview ? (
        <div className="spinner" style={{ marginTop: '24px' }} />
      ) : (
        <>
          <div className="analytics-cards">
            <div className="analytics-card">
              <div className="analytics-card-label">جلسات</div>
              <div className="analytics-card-value">{overview?.sessions ?? 0}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-label">مشاهدات صفحات</div>
              <div className="analytics-card-value">{overview?.pageviews ?? 0}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-label">متوسط الصفحات / جلسة</div>
              <div className="analytics-card-value">{avgPagesPerSession ?? '—'}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-label">الارتداد</div>
              <div className="analytics-card-value">
                {overview?.bounce_rate != null ? `${(overview.bounce_rate * 100).toFixed(1)}%` : '—'}
              </div>
            </div>
          </div>

          <div className="analytics-chart-wrap">
            <h2 className="analytics-chart-title">نشاط يومي</h2>
            {lineEmpty ? (
              <div className="analytics-chart-empty" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
                  <XAxis dataKey="day" tick={axisTick} axisLine={axisLine} tickLine={{ stroke: '#e8ecf1' }} />
                  <YAxis tick={axisTick} axisLine={axisLine} tickLine={{ stroke: '#e8ecf1' }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipContentStyle} />
                  <Legend wrapperStyle={legendStyle} />
                  <Line type="monotone" dataKey="pageviews" stroke="#e31e24" strokeWidth={2} dot={false} name="مشاهدات" />
                  <Line type="monotone" dataKey="sessions" stroke="#94a3b8" strokeWidth={2} dot={false} name="جلسات" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="analytics-charts-grid">
            <div className="analytics-chart-wrap analytics-chart-panel">
              <h2 className="analytics-chart-title">السيارات الأكثر تصفّحاً</h2>
              {carsChartIsPathFallback && (
                <p className="analytics-chart-hint" style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b' }}>
                  الخادم لا يزال بدون نقطة «أنواع السيارات»؛ يُعرض مؤقتاً أكثر المسارات حتى تحديث الـ API.
                </p>
              )}
              {carsBarData.length === 0 ? (
                <div className="analytics-chart-empty analytics-chart-empty--sm" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, carsBarData.length * 36)}>
                  <BarChart
                    layout="vertical"
                    data={carsBarData}
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={axisTick}
                      axisLine={axisLine}
                      tickLine={{ stroke: '#e8ecf1' }}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={120}
                      tick={axisTickSm}
                      axisLine={axisLine}
                      tickLine={{ stroke: '#e8ecf1' }}
                      interval={0}
                    />
                    <Tooltip contentStyle={tooltipContentStyle} />
                    <Bar dataKey="views" name="مشاهدات" fill="#e31e24" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="analytics-chart-wrap analytics-chart-panel">
              <h2 className="analytics-chart-title">الأجهزة</h2>
              {devicesBarData.length === 0 ? (
                <div className="analytics-chart-empty analytics-chart-empty--sm" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, devicesBarData.length * 40)}>
                  <BarChart
                    layout="vertical"
                    data={devicesBarData}
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={axisTick}
                      axisLine={axisLine}
                      tickLine={{ stroke: '#e8ecf1' }}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={88}
                      tick={axisTick}
                      axisLine={axisLine}
                      tickLine={{ stroke: '#e8ecf1' }}
                      interval={0}
                    />
                    <Tooltip contentStyle={tooltipContentStyle} />
                    <Bar dataKey="sessions" name="جلسات" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
